'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';

interface AssetDetailPanelProps {
  asset: any;
  onClose: () => void;
  onQuarantine: (asset: any) => void;
}

export default function AssetDetailPanel({ asset, onClose, onQuarantine }: AssetDetailPanelProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const riskColor = asset.riskScore >= 80 ? 'text-red-400' : asset.riskScore >= 60 ? 'text-orange-400' : asset.riskScore >= 30 ? 'text-amber-400' : 'text-emerald-400';
  const riskBarColor = asset.riskScore >= 80 ? 'bg-red-500' : asset.riskScore >= 60 ? 'bg-orange-500' : asset.riskScore >= 30 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-zinc-950 border-l border-zinc-800 z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-800 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-zinc-200">{asset.name}</span>
              <StatusBadge status={asset.status} size="md" />
            </div>
            <p className="font-mono text-xs text-amber-400/80 mt-0.5">{asset.ip}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors ml-4">
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>

        {/* Risk Score Bar */}
        <div className="px-6 py-3 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600">Risk Score</span>
            <span className={`font-mono text-lg font-bold tabular-nums ${riskColor}`}>{asset.riskScore} / 100</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${riskBarColor}`} style={{ width: `${asset.riskScore}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin space-y-5">
          {/* Asset Info Grid */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Asset Details</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Asset ID', value: asset.id, mono: true },
                { label: 'Type', value: asset.type.charAt(0).toUpperCase() + asset.type.slice(1), mono: false },
                { label: 'MAC Address', value: asset.mac, mono: true },
                { label: 'OS / Firmware', value: asset.os, mono: false },
                { label: 'Location', value: asset.location, mono: true },
                { label: 'Owner', value: asset.owner, mono: false },
              ].map((f) => (
                <div key={f.label} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wide mb-1">{f.label}</p>
                  <p className={`text-xs font-medium text-zinc-300 ${f.mono ? 'font-mono' : ''} break-all`}>{f.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Description</h4>
            <p className="text-sm text-zinc-400 leading-relaxed">{asset.description}</p>
          </div>

          {/* Open Ports */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Open Ports</h4>
            {asset.openPorts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {asset.openPorts.map((port: number) => (
                  <span key={port} className="font-mono text-xs bg-zinc-900 border border-zinc-800 text-zinc-400 px-2.5 py-1 rounded-md">
                    {port}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-zinc-600 italic">No open ports detected or asset is isolated</p>
            )}
          </div>

          {/* Tags */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Tags</h4>
            <div className="flex flex-wrap gap-2">
              {asset.tags.map((tag: string) => (
                <span key={tag} className={`text-[11px] font-medium px-2 py-0.5 rounded border ${
                  tag === 'quarantined' || tag === 'c2-suspected' ? 'bg-purple-500/15 text-purple-400 border-purple-500/20' :
                  tag === 'critical' || tag === 'under-attack' ? 'bg-red-500/15 text-red-400 border-red-500/20' :
                  tag === 'prod'? 'bg-blue-500/15 text-blue-400 border-blue-500/20' : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                }`}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Alert Summary */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Alert Summary</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-center">
                <p className={`text-2xl font-bold tabular-nums ${asset.openAlerts > 0 ? 'text-orange-400' : 'text-zinc-600'}`}>{asset.openAlerts}</p>
                <p className="text-[11px] text-zinc-600 mt-0.5">Open Alerts</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-center">
                <p className={`text-2xl font-bold tabular-nums ${asset.criticalAlerts > 0 ? 'text-red-400' : 'text-zinc-600'}`}>{asset.criticalAlerts}</p>
                <p className="text-[11px] text-zinc-600 mt-0.5">Critical Alerts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-zinc-800 flex items-center gap-3">
          {asset.status !== 'quarantined' && (
            <button
              onClick={() => onQuarantine(asset)}
              className="px-3 py-2 bg-purple-600/20 border border-purple-500/30 hover:bg-purple-600/30 text-purple-400 text-xs font-semibold rounded-lg transition-colors"
            >
              Quarantine Asset
            </button>
          )}
          <a
            href={`/event-monitoring`}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-lg transition-colors text-center"
          >
            <Icon name="MagnifyingGlassIcon" size={13} />
            View Events for Asset
          </a>
        </div>
      </div>
    </>
  );
}